import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
  fetchStateData,
  CreateDistrictService,
  CreateDistrictPayload,
} from "../Services/APIService";

interface CreateCardProps {
  onCreateComplete: () => void;
  onCancel: () => void;
}

interface State {
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface FormData {
  districtName: string;
  stateID: number | null;
}

const CreateDistrictCard: React.FC<CreateCardProps> = ({
  onCreateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    districtName: "",
    stateID: null,
  });

  const [states, setStates]               = useState<State[]>([]);
  const [isLoading, setIsLoading]         = useState(false);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [error, setError]                 = useState<string | null>(null);

  const requiredField = <span className="text-danger ms-1">*</span>;

  // Fetch states on mount
  useEffect(() => {
    const getStates = async () => {
      setIsLoadingStates(true);
      try {
        const stateData = await fetchStateData();
        const active = stateData.filter((s: State) => s.isActive);
        setStates(active);
        if (active.length > 0) {
          setFormData((prev) => ({ ...prev, stateID: active[0].stateId }));
        }
      } catch {
        setError("Failed to fetch states");
      } finally {
        setIsLoadingStates(false);
      }
    };
    getStates();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "stateID" ? parseInt(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.districtName.trim()) {
      setError("Please enter a valid District Name");
      return;
    }

    if (!formData.stateID) {
      setError("Please select a State");
      return;
    }

    const userId = localStorage.getItem("UserId");
    if (!userId) {
      setError("User ID not found. Please log in again.");
      return;
    }

    const payload: CreateDistrictPayload = {
      districtName: formData.districtName.trim().replace(/\s+/g, " "),
      stateID:      formData.stateID,
      createdBy:    parseInt(userId),
    };

    try {
      setIsLoading(true);
      const result = await CreateDistrictService(payload);

      if (result.isCreated) {
        toast.success(result.message || "District Created Successfully!");
        setFormData({
          districtName: "",
          stateID: states.length > 0 ? states[0].stateId : null,
        });
        onCreateComplete();
      } else {
        throw new Error(result.message || "Failed to create district");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({ districtName: "", stateID: null });
    setError(null);
    onCancel();
  };

  return (
    <div className="card mb-4">
      <div className="card-header carHeaderWithBtn">
        <h5 className="card-title mb-0">Create District</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger text-danger" role="alert">
              {error}
            </div>
          )}
          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="stateID" className="form-label">
                State Name{requiredField}
              </label>
              {isLoadingStates ? (
                <div className="text-muted">Loading states...</div>
              ) : (
                <select
                  id="stateID"
                  name="stateID"
                  className="form-select form-control-lg"
                  value={formData.stateID ?? ""}
                  onChange={handleInputChange}
                  required
                >
                  {states.length === 0 ? (
                    <option value="" disabled>
                      No states available
                    </option>
                  ) : (
                    states.map((state) => (
                      <option key={state.stateId} value={state.stateId}>
                        {state.stateName}
                      </option>
                    ))
                  )}
                </select>
              )}
            </div>
            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="districtName" className="form-label">
                District Name{requiredField}
              </label>
              <input
                type="text"
                id="districtName"
                name="districtName"
                className="form-control form-control-lg"
                placeholder="Enter District Name"
                value={formData.districtName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="col-12 mt-3">
              <button
                type="submit"
                className="btn btn-primary me-2"
                disabled={isLoading || isLoadingStates}
              >
                {isLoading ? "Creating..." : "Submit"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateDistrictCard;